import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useState } from 'react'
import type { SupportedLocale } from '../../../i18n/types'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { getPreferences, patchPreferences } from '../api/preferencesClient'
import { readStoredLocale, writeStoredLocale } from '../utils/localePreferenceStorage'
import { pickInitialLocale } from '../utils/pickInitialLocale'

export type PreferencesState =
  | { status: 'loading' }
  | {
      status: 'ready'
      available: boolean
      locale: SupportedLocale
      stored: SupportedLocale | null
    }

export type UsePreferencesResult = {
  state: PreferencesState
  updateLanguage: (next: SupportedLocale) => Promise<void>
}

export function usePreferences(): UsePreferencesResult {
  const apiClient = useFamilyPortalApiClient()
  const [state, setState] = useState<PreferencesState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false

    getPreferences(apiClient)
      .then((result) => {
        if (cancelled) return
        if (!result.available) {
          // No authoritative server value — fall back to the previously stored locale (if any),
          // then to the browser default. Do NOT overwrite storage here: a saved 'es' must not
          // be replaced by the browser default just because the prefs endpoint is unavailable.
          setState({
            status: 'ready',
            available: false,
            locale: pickInitialLocale(readStoredLocale()),
            stored: null,
          })
          return
        }

        const locale = pickInitialLocale(result.language ?? readStoredLocale())
        if (result.language) {
          writeStoredLocale(locale)
        }
        setState({
          status: 'ready',
          available: true,
          locale,
          stored: result.language,
        })
      })
      .catch((err) => {
        if (cancelled) return
        Sentry.captureException(err, { tags: { feature: 'preferences', stage: 'boot-fetch' } })
        setState({
          status: 'ready',
          available: false,
          locale: pickInitialLocale(readStoredLocale()),
          stored: null,
        })
      })

    return () => {
      cancelled = true
    }
  }, [apiClient])

  const updateLanguage = useCallback(
    async (next: SupportedLocale) => {
      const saved = await patchPreferences(apiClient, next)
      writeStoredLocale(saved)
      setState({
        status: 'ready',
        available: true,
        locale: saved,
        stored: saved,
      })
    },
    [apiClient]
  )

  return { state, updateLanguage }
}
